/**
 * A document's structured fields, as something you can actually follow.
 *
 * `JSON.stringify(data, null, 2)` in a <pre> is the honest minimum and it fails
 * at the one thing this project does constantly: documents reference each
 * other by id. A review's fields are full of opaque strings, and reading them
 * meant copying an id out and pasting it somewhere else to learn it was a plan
 * you wrote yesterday.
 *
 * So: a value that IS a document in this project renders as that document —
 * its title, its type — and expands in place to show its own fields. Following
 * a reference is a disclosure triangle rather than a round trip.
 *
 * Resolution is by catalog membership, not by pattern-matching what an id looks
 * like. A string that merely resembles an id is left alone, so a branch name or
 * a hash never pretends to be a link.
 */

import type { AnyDocumentId } from '@automerge/automerge-repo'
import {
  type DocumentRef,
  readDocumentContent,
  readDocumentData,
} from '@mod/core/model'
import { ChevronDown, ChevronRight, FileText } from 'lucide-react'
import { useState } from 'react'
import { useDocument } from '../../services/world/repo/useDocument'

/** How deep a chain of references may be followed before it stops offering to
 *  go further. Reviews point at plans that point at documents; three levels is
 *  past anything real, and a cycle would otherwise recurse forever. */
const MAX_DEPTH = 3

export function FieldsView({
  data,
  content,
  documents,
  records,
  depth = 0,
}: {
  data: Record<string, unknown> | undefined
  /** The document's body, shown after its fields when it has one (RFC 0271). */
  content?: string
  /** The project catalog, so a value can be recognised as a document. */
  documents: DocumentRef[]
  /**
   * The RECORDS this document holds, when it is an app view rather than a
   * record itself.
   *
   * An app view has no fields of its own — its data is the instances it
   * manages, which the rendered app shows as a list and which were otherwise
   * invisible here. So Data on an app answers "what does this app hold", and
   * Data on one of those records answers "what is in it".
   */
  records?: DocumentRef[]
  depth?: number
}) {
  const entries = Object.entries(data ?? {})

  if (records) {
    if (records.length === 0) {
      return (
        <p className="p-3 text-muted-foreground/60 text-xs">
          This app has no records yet.
        </p>
      )
    }
    return (
      <div className="p-2">
        <p className="mb-1 px-1 font-medium text-[10px] text-muted-foreground/60 uppercase tracking-wide">
          {records.length} record{records.length === 1 ? '' : 's'}
        </p>
        <ul className="flex flex-col gap-px">
          {records.map((r) => (
            <RefField key={r.id} refDoc={r} documents={documents} depth={0} />
          ))}
        </ul>
      </div>
    )
  }

  if (entries.length === 0 && !content) {
    return (
      <p className="p-3 text-muted-foreground/60 text-xs">
        No fields on this document. Its text is under Source.
      </p>
    )
  }

  return (
    <div className="p-2">
      {entries.length > 0 && (
        <ul className="flex flex-col gap-px">
          {entries.map(([key, value]) => (
            <Field
              key={key}
              name={key}
              value={value}
              documents={documents}
              depth={depth}
            />
          ))}
        </ul>
      )}
      {content && (
        <div className="mt-2 border-border/60 border-t pt-2">
          <p className="mb-1 px-1 font-medium text-[10px] text-muted-foreground/60 uppercase tracking-wide">
            Body
          </p>
          <pre className="whitespace-pre-wrap break-words px-1 font-mono text-[12px] text-muted-foreground leading-relaxed">
            {content}
          </pre>
        </div>
      )}
    </div>
  )
}

function Field({
  name,
  value,
  documents,
  depth,
}: {
  name: string
  value: unknown
  documents: DocumentRef[]
  depth: number
}) {
  // A reference is a value that names a document in THIS project. Checked by
  // membership rather than shape, so `sourceBranch: "feat/x"` stays text.
  const ref =
    typeof value === 'string'
      ? documents.find((d) => d.id === value)
      : undefined

  if (ref && depth < MAX_DEPTH) {
    return (
      <RefField name={name} refDoc={ref} documents={documents} depth={depth} />
    )
  }

  const nested =
    value && typeof value === 'object' && !Array.isArray(value)
      ? (value as Record<string, unknown>)
      : undefined

  return (
    <li className="px-1 py-0.5">
      <div className="flex items-baseline gap-2">
        <span className="shrink-0 font-mono text-[11px] text-muted-foreground/70">
          {name}
        </span>
        {nested ? null : (
          <span className="min-w-0 break-words font-mono text-[12px] text-foreground/90">
            {renderScalar(value)}
          </span>
        )}
      </div>
      {nested && (
        <div className="ml-3 border-border/40 border-l pl-2">
          <FieldsView data={nested} documents={documents} depth={depth + 1} />
        </div>
      )}
    </li>
  )
}

/** A field whose value is another document: shown by title, expandable in place. */
function RefField({
  name,
  refDoc,
  documents,
  depth,
}: {
  /** The field this document was referenced BY. Absent when the document is
   *  itself the row (a record in an app's list), where a label would just
   *  repeat the title. */
  name?: string
  refDoc: DocumentRef
  documents: DocumentRef[]
  depth: number
}) {
  const [open, setOpen] = useState(false)
  // Hydrated only once expanded: a review with five references should not pull
  // five documents to render a collapsed list.
  const [doc] = useDocument(open ? (refDoc.id as AnyDocumentId) : undefined)

  return (
    <li className="px-1 py-0.5">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-baseline gap-2 rounded text-left hover:bg-muted/50"
      >
        {name && (
          <span className="shrink-0 font-mono text-[11px] text-muted-foreground/70">
            {name}
          </span>
        )}
        <span className="flex min-w-0 items-center gap-1 text-[12px] text-foreground/90">
          {open ? (
            <ChevronDown size={11} className="shrink-0" />
          ) : (
            <ChevronRight size={11} className="shrink-0" />
          )}
          <FileText size={11} className="shrink-0 text-muted-foreground/60" />
          <span className="truncate">{refDoc.title}</span>
          {refDoc.dataType && (
            <span className="shrink-0 text-[10px] text-muted-foreground/50 uppercase">
              {refDoc.dataType}
            </span>
          )}
        </span>
      </button>
      {open && (
        <div className="ml-3 border-border/40 border-l pl-2">
          {doc === undefined ? (
            <p className="px-1 py-1 text-[11px] text-muted-foreground/60">
              Loading…
            </p>
          ) : (
            <FieldsView
              data={readDocumentData(doc)}
              content={readDocumentContent(doc)}
              documents={documents}
              depth={depth + 1}
            />
          )}
        </div>
      )}
    </li>
  )
}

function renderScalar(value: unknown): string {
  if (value === null) return 'null'
  if (value === undefined) return 'undefined'
  if (Array.isArray(value)) return `[${value.length}]`
  if (typeof value === 'string') return value
  return JSON.stringify(value)
}
